// Micro-movement from the phase of the beat signal (Radar analysis). The
// beat signal is one real channel of 512 samples across the radar's sweep;
// an echo at a steady distance is a tone at a steady frequency, and a change
// in that distance far smaller than a range cell shows as a change in the
// tone's phase: delta-phase = 4 pi delta-R / lambda (as in InSAR), so one
// full turn is lambda / 2 = 1.87 mm of range and one degree 5.2 um at
// 80 GHz. Pure helpers: no React, network or storage.
//
// For a series of beat signals: the echo's position f* (fractional radar
// bins) is the median over the captures of the strongest echo beyond the
// near field (Hann window, x16 zero-padding, a parabola through the peak);
// each capture's complex value at f* is
//   A = sum_n x[n] hann[n] exp(-2j pi f* n / 512),  x = adc - mean,
// its phase angle(A) is unwrapped over time, and the range change is
// -(phase - phase[0]) lambda / (4 pi). The sign is relative: which way is
// "down" is still to be confirmed with a known level change.
import { finite, timeOf } from "utils/radarDiagnostics";
import { hasSpectrum } from "utils/radarStream";
import {
  NEAR_FIELD_BINS,
  beatSpectrum,
  compareSpectra,
  quantile,
} from "utils/radarSignal";

export const WAVELENGTH_MM = 3.747; // 80 GHz
export const UM_PER_RAD = (WAVELENGTH_MM * 1000) / (4 * Math.PI); // 298.2
export const UM_PER_TURN = UM_PER_RAD * 2 * Math.PI; // 1,873.5 um
export const PHASE_PAD = 16;
// At most this many beat signals in one measurement (the newest).
export const PHASE_CAPTURES = 600;
// Fewer samples than this after the clipping are too few for an estimate.
export const PHASE_MIN_SAMPLES = 64;
// The echo's position is the median over at most this many captures,
// spread across the series (each costs an 8,192-point FFT).
export const BIN_SAMPLE = 120;

const TWO_PI = 2 * Math.PI;
const numbers = (samples) =>
  Array.from(samples || [], (v) => Number(v)).filter(Number.isFinite);

// The periodic Hann window of m points, as our FFT of the beat signal uses.
export function hann(m) {
  const w = new Float64Array(m);
  for (let i = 0; i < m; i++) w[i] = 0.5 - 0.5 * Math.cos((TWO_PI * i) / m);
  return w;
}

// The strongest echo beyond the near field in one beat signal (samples
// `from` on), in fractional radar bins: Hann, x16 zero-padding and a parabola
// through the peak. Null without one.
export function echoBin(samples, { from = 0 } = {}) {
  const fft = beatSpectrum(numbers(samples), { pad: PHASE_PAD, from });
  const bin = compareSpectra(null, fft).ours?.bin;
  return Number.isFinite(bin) && bin >= NEAR_FIELD_BINS ? bin : null;
}

// The complex value of a beat signal at fractional bin `bin`, over samples
// `from` to the end with their own mean removed and their own Hann window,
// the exponent on the sample's place in the whole record (n of N), so that
// captures cut at different samples keep one phase reference. -> {re, im,
// phase (radians, -pi to pi), amplitude (counts)}, or null when too short.
export function toneAt(samples, bin, { from = 0 } = {}) {
  const x = numbers(samples);
  const n = x.length;
  const start = Math.max(0, Math.min(n, Math.floor(from) || 0));
  const m = n - start;
  if (m < 8 || !Number.isFinite(bin)) return null;
  let mean = 0;
  for (let i = start; i < n; i++) mean += x[i];
  mean /= m;
  const w = hann(m);
  let re = 0;
  let im = 0;
  let gain = 0;
  for (let i = 0; i < m; i++) {
    const k = start + i;
    const angle = (TWO_PI * bin * k) / n;
    const v = (x[k] - mean) * w[i];
    re += v * Math.cos(angle);
    im -= v * Math.sin(angle);
    gain += w[i];
  }
  return {
    re,
    im,
    phase: Math.atan2(im, re),
    amplitude: (2 * Math.hypot(re, im)) / gain,
  };
}

// Phases with the jumps of a full turn taken out: each step is taken as the
// one between -pi and pi.
export function unwrap(phases) {
  const out = [];
  let offset = 0;
  (phases || []).forEach((p, i) => {
    if (i) {
      const step = p - phases[i - 1];
      offset -= TWO_PI * Math.round(step / TWO_PI);
    }
    out.push(p + offset);
  });
  return out;
}

// The least-squares line through (x, y): {slope, intercept}, or null with
// fewer than two distinct x.
export function linearTrend(xs, ys) {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return null;
  let mx = 0;
  let my = 0;
  for (let i = 0; i < n; i++) {
    mx += xs[i];
    my += ys[i];
  }
  mx /= n;
  my /= n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    sxx += (xs[i] - mx) ** 2;
    sxy += (xs[i] - mx) * (ys[i] - my);
  }
  if (!(sxx > 0)) return null;
  const slope = sxy / sxx;
  return { slope, intercept: my - slope * mx };
}

// The phase noise of one capture: the standard deviation of the steps
// between consecutive captures over the square root of two (each step holds
// two captures' noise). A steady drift only moves the steps' mean when the
// captures are evenly spaced; with `times` (ms) and `slope` (radians per ms)
// each step loses the drift over its own gap first, so bursts minutes apart
// do not count as noise. Radians, or null with fewer than three captures.
export function phaseNoise(unwrapped, { times = null, slope = 0 } = {}) {
  const steps = (unwrapped || [])
    .slice(1)
    .map(
      (p, i) =>
        p - unwrapped[i] - (times ? slope * (times[i + 1] - times[i]) : 0),
    );
  if (steps.length < 2) return null;
  const mean = steps.reduce((a, b) => a + b, 0) / steps.length;
  const variance =
    steps.reduce((a, b) => a + (b - mean) ** 2, 0) / (steps.length - 1);
  return Math.sqrt(variance) / Math.SQRT2;
}

// A step of more than a quarter turn (0.47 mm of range) between consecutive
// captures may have wrapped: the phase cannot tell it from the opposite way.
export const WRAP_RISK_RAD = Math.PI / 2;

// Where the samples after a capture's clipping start (its last clipped
// sample + 1), 0 when it does not clip.
export const unclippedFrom = (clip) =>
  clip?.clipped && Number.isFinite(clip.last) ? clip.last + 1 : 0;

// A series of beat signals -> the micro-movement of their strongest echo.
// `captures`: [{id, ms, samples, clip, step, cal}] (clip as clipFrom() gives
// it, the server's else ours; step and cal the capture's RangeRes and
// calibration offset in mm). With `unclipped`, each capture is used from its
// last clipped sample on (dropped with fewer than 64 left), with its own mean
// and Hann window and the echo found on those samples too.
// -> {bin, mm, step, cal, points [{id, ms, phase, um}], trend {umPerHour,
//    intercept}, noise {rad, deg, um}, used, skipped, risky (steps of more
//    than a quarter turn), from, to}, or null.
export function phaseSeries(
  captures,
  { unclipped = false, sample = BIN_SAMPLE } = {},
) {
  const list = (captures || [])
    .filter(
      (c) =>
        c &&
        Number.isFinite(c.ms) &&
        Array.isArray(c.samples) &&
        c.samples.length >= 8,
    )
    .map((c) => ({
      ...c,
      from: unclipped ? unclippedFrom(c.clip) : 0,
    }))
    .sort((a, b) => a.ms - b.ms);
  const usable = list.filter(
    (c) => c.samples.length - c.from >= PHASE_MIN_SAMPLES,
  );
  if (!usable.length) return null;
  // The echo: the median over captures spread across the series.
  const every = Math.max(1, Math.ceil(usable.length / sample));
  const bins = usable
    .filter((_, i) => i % every === 0)
    .map((c) => echoBin(c.samples, { from: c.from }))
    .filter((b) => b !== null);
  const bin = quantile(bins, 0.5);
  if (bin === null) return null;
  const tones = usable
    .map((c) => ({ c, tone: toneAt(c.samples, bin, { from: c.from }) }))
    .filter((t) => t.tone && t.tone.amplitude > 0);
  if (!tones.length) return null;
  const phases = unwrap(tones.map((t) => t.tone.phase));
  const points = tones.map((t, i) => ({
    id: t.c.id ?? null,
    ms: t.c.ms,
    phase: phases[i],
    um: -(phases[i] - phases[0]) * UM_PER_RAD,
  }));
  const fit = linearTrend(
    points.map((p) => (p.ms - points[0].ms) / 3600000),
    points.map((p) => p.um),
  );
  // The drift in radians per ms, for the noise.
  const slope = fit ? -fit.slope / UM_PER_RAD / 3600000 : 0;
  const noise = phaseNoise(phases, { times: points.map((p) => p.ms), slope });
  const risky = phases
    .slice(1)
    .filter((p, i) => Math.abs(p - phases[i]) > WRAP_RISK_RAD).length;
  const ref = usable.find((c) => Number.isFinite(c.step)) || {};
  const step = Number.isFinite(ref.step) ? ref.step : null;
  const cal = Number.isFinite(ref.cal) ? ref.cal : 0;
  return {
    bin,
    step,
    cal,
    mm: step === null ? null : bin * step - cal,
    points,
    trend: fit && { umPerHour: fit.slope, intercept: fit.intercept },
    noise:
      noise === null
        ? null
        : { rad: noise, deg: (noise * 180) / Math.PI, um: noise * UM_PER_RAD },
    used: points.length,
    skipped: list.length - points.length,
    risky,
    from: points[0].ms,
    to: points[points.length - 1].ms,
  };
}

// ---- Finding the beat signals of a window ----

// The stream endpoint's page (radar/stream/?since=&limit=300&mode=).
export const LIST_PAGE = 300;
const withBeat = (list) =>
  list.filter((c) => c?.has_adc && c.id !== null && c.id !== undefined);
// The captures with a beat signal from `from` (ms) on, oldest first, enough
// for the newest `want`: the newest page first (the endpoint without
// since); when the window reaches further back, from where the newest page's
// rate of beat signals says `want` of them begin (a quarter more), paging
// forward while pages come back full, at most `maxPages` in all.
// `fetchPage(since)` returns the endpoint's answer, since null for the
// newest page. -> {rows, complete (false when the paging stopped short),
// pages, step (the newest spectrum's range step among the captures listed:
// a beat signal alone carries none, and the server's assumed 30 mm would
// put the echo about 5 % short; null without one)}
export async function listBeatSignals(
  fetchPage,
  { from, want = PHASE_CAPTURES, maxPages = 30 } = {},
) {
  const timed = (data) =>
    (Array.isArray(data?.captures) ? data.captures : []).filter(
      (c) => timeOf(c?.t) !== null,
    );
  const head = timed(await fetchPage(null));
  const inWindow = (c) => timeOf(c.t) >= from;
  const stepOf = (list) =>
    finite(
      [...list].reverse().find((c) => hasSpectrum(c) && finite(c?.step_mm))
        ?.step_mm,
    );
  if (head.length < LIST_PAGE || timeOf(head[0].t) < from)
    return {
      rows: withBeat(head.filter(inWindow)),
      complete: true,
      pages: 1,
      step: stepOf(head),
    };
  const first = timeOf(head[0].t);
  const last = timeOf(head[head.length - 1].t);
  const share = Math.max(withBeat(head).length / head.length, 0.02);
  const perMs = head.length / Math.max(1, last - first);
  let since = Math.max(from, last - ((want / share) * 1.25) / perMs);
  const rows = [];
  const seen = new Set(head.map((c) => String(c.id)));
  let pages = 1;
  let complete = true;
  while (since < first) {
    if (pages >= maxPages) {
      complete = false;
      break;
    }
    const page = timed(await fetchPage(new Date(since).toISOString()));
    pages += 1;
    for (const c of page)
      if (timeOf(c.t) < first && !seen.has(String(c.id))) {
        seen.add(String(c.id));
        rows.push(c);
      }
    const end = page.length ? timeOf(page[page.length - 1].t) : null;
    if (page.length < LIST_PAGE || end === null || end <= since) break;
    since = end;
  }
  return {
    rows: withBeat([...rows, ...head]).filter(inWindow),
    complete,
    pages,
    step: stepOf([...rows, ...head]),
  };
}
